/**
 * aides-entreprises.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /entreprises/aides/[slug].
 *
 * Ciblage : ETI / grands comptes multi-sites (immeubles tertiaires +
 * sites industriels). Couvre le panorama des aides cumulables.
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_ENTREPRISES: AideStub[] = [
  // ══════════════════════════════════════════════════════════════
  // 1. CEE BAT-* (tertiaire multi-sites)
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'cee-bat',
    iconName: 'building',
    eyebrow: "Certificats d'Économies d'Énergie, bâtiments tertiaires,",
    title: 'CEE BAT-* (tertiaire multi-sites)',
    shortLabel: 'CEE BAT',
    lead: 'Des aides pour vos travaux dans vos bureaux, commerces, hôtels, agences. Si vous avez plusieurs sites, on peut déposer tous vos dossiers en une fois.',
    amount: 'Selon kWhc cumac',
    amountLabel: 'Versement après dépôt validé',
    keyPoints: [
      {
        iconName: 'building',
        label: 'Catalogue BAT complet',
        description:
          'Chauffage (pompe à chaleur, pilotage, chaudière, réseau de chaleur), enveloppe (isolation, toitures-terrasses), équipements (LED, variateurs, programmateurs).',
      },
      {
        iconName: 'sparkles',
        label: 'Bonus pilotage',
        description:
          'Majoration pour le pilotage intelligent de niveau avancé ou haute performance.',
      },
      {
        iconName: 'gauge',
        label: 'Dépôt consolidé multi-sites',
        description:
          'Plusieurs sites instruits dans un même dossier : un seul engagement, moins de frais, plus de volume.',
      },
      {
        iconName: 'shield-check',
        label: 'Conservation 9 ans',
        description: 'Pièces conservées 9 ans — coffre-fort numérique recommandé.',
      },
    ],
    conditions: [
      'Bâtiment tertiaire existant (bureau, commerce, hôtel)',
      'Opération éligible à une fiche BAT-* applicable',
      'Engagement du dossier avant signature du devis travaux',
      "Conservation de l'ensemble des justificatifs 9 ans",
      "Qualification professionnelle de l'installateur si exigée",
    ],
    cumulableWith: [
      'Fonds chaleur ADEME',
      'Fonds décarbonation industrie',
      'Avantage fiscal',
      'Aides régionales',
    ],
    faq: [
      {
        question: 'Faut-il une qualification spécifique ?',
        answer:
          "Pas systématiquement. Certaines opérations exigent des qualifications spécifiques mais ce n'est pas un prérequis général. Vérifier au cas par cas.",
      },
      {
        question: 'Comment optimiser un dépôt multi-sites ?',
        answer:
          "Le délégataire regroupe l'ensemble des opérations dans un dossier unique. Vous captez les avantages de volume, payez moins de frais et le délai global se réduit. C'est l'approche standard pour les ETI multi-sites.",
      },
      {
        question: "L'aide est-elle cumulable avec le Fonds chaleur ?",
        answer:
          "Oui, dans la limite des plafonds autorisés. Pour les opérations chaleur (pompe à chaleur, biomasse, géothermie, réseau de chaleur), le Fonds chaleur finance la part équipement et l'aide travaux vient en complément.",
      },
    ],
    ctaLabel: 'Lancer le diagnostic CEE BAT',
    ctaHref: '/entreprises/diagnostic-cee',
    secondaryCta: { label: 'Voir le catalogue BAT', href: '/cee/fiches?secteur=BAT' },
  },
  // ══════════════════════════════════════════════════════════════
  // 2. CEE IND-* (industrie multi-sites)
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'cee-ind',
    iconName: 'cog',
    eyebrow: "Certificats d'Économies d'Énergie, sites industriels,",
    title: 'CEE IND-* (industrie multi-sites)',
    shortLabel: 'CEE IND',
    lead: "Des aides pour vos équipements industriels : moteurs, variateurs, récupération de chaleur, isolation des réseaux. Bonus si vous avez une démarche de management de l'énergie.",
    amount: 'Selon kWhc cumac',
    amountLabel: 'Versement après dépôt validé',
    keyPoints: [
      {
        iconName: 'cog',
        label: 'Utilités industrielles (IND-UT)',
        description:
          'Air comprimé (compresseurs haute efficacité, traitement des fuites), vapeur (purgeurs, calorifugeage), froid (récupération chaleur), ventilation (variateurs).',
      },
      {
        iconName: 'gauge',
        label: 'Bâti industriel (IND-BA)',
        description:
          'Isolation des bâtiments, étanchéité, éclairage halls de production, ventilation à la demande — opérations souvent volumineuses en m².',
      },
      {
        iconName: 'sparkles',
        label: "Bonus management de l'énergie",
        description:
          "Les opérations couplées à une démarche certifiée de management de l'énergie bénéficient d'une majoration.",
      },
      {
        iconName: 'flame',
        label: 'Récupération chaleur fatale',
        description:
          "Fiches IND-UT dédiées à la récupération de chaleur sur process (compresseurs, fours, groupes froid) — opérations à fort volume d'économies.",
      },
    ],
    conditions: [
      'Site industriel en activité (établissement de code NAF section C)',
      'Opération éligible à une fiche IND-* applicable',
      'Engagement du dossier avant signature du devis travaux',
      'Conservation des pièces 9 ans',
      "Démarche de management de l'énergie documentée si bonus mobilisé",
    ],
    cumulableWith: [
      'Fonds décarbonation industrie',
      'Fonds chaleur ADEME',
      'Avantage fiscal',
      'Aides régionales',
    ],
    faq: [
      {
        question: "Comment fonctionne le bonus management de l'énergie ?",
        answer:
          "Les fiches IND-* prévoient une majoration sur les opérations réalisées dans un site couvert par un système de management de l'énergie certifié. Le coefficient de majoration peut faire nettement grimper la rentabilité de l'opération.",
      },
      {
        question: "L'aide IND est-elle cumulable avec le Fonds décarbonation ?",
        answer:
          "Oui, dans la limite des plafonds autorisés. Le Fonds décarbonation finance les projets structurants (souvent millions d'€), l'aide IND complète l'enveloppe sur les opérations d'efficacité standardisées.",
      },
      {
        question: "Quelle différence entre l'aide de base et le bonus IND ?",
        answer:
          'Les fiches IND classiques rapportent un volume standard. Certains bonus majorent ce volume pour des opérations ciblées (chaleur fatale ou moteurs par exemple). À vérifier à la date de signature du devis.',
      },
    ],
    ctaLabel: 'Lancer le diagnostic CEE IND',
    ctaHref: '/entreprises/diagnostic-cee',
    secondaryCta: { label: 'Voir le catalogue IND', href: '/cee/fiches?secteur=IND' },
  },
  // ══════════════════════════════════════════════════════════════
  // 3. Fonds chaleur ADEME
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'fonds-chaleur',
    iconName: 'flame',
    eyebrow: 'Fonds chaleur, ADEME, énergies renouvelables thermiques,',
    title: 'Fonds chaleur ADEME',
    shortLabel: 'Fonds chaleur',
    lead: 'Une aide pour financer vos projets de chaleur renouvelable : biomasse, géothermie, solaire thermique, récupération de chaleur. Le principal levier pour décarboner votre chauffage.',
    amount: 'Selon barème ADEME',
    amountLabel: "Aide à l'investissement, plafonnée",
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Biomasse industrielle et tertiaire',
        description:
          "Chaufferies bois-énergie, plaquettes forestières, granulés. Critères de qualité de l'air à respecter.",
      },
      {
        iconName: 'gauge',
        label: 'Géothermie et PAC',
        description:
          'Géothermie de surface (pompes à chaleur sur sondes ou nappes) et géothermie profonde pour réseaux de chaleur.',
      },
      {
        iconName: 'sparkles',
        label: 'Solaire thermique',
        description:
          'Capteurs solaires pour eau chaude collective et process industriels (séchage, lavage, pré-chauffage).',
      },
      {
        iconName: 'percent',
        label: 'Réseaux de chaleur',
        description:
          'Création, extension ou densification de réseaux de chaleur urbains majoritairement renouvelables.',
      },
    ],
    conditions: [
      'Projet de production de chaleur renouvelable ou de récupération de chaleur',
      "Étude de faisabilité réalisée par un bureau d'études compétent",
      "Plan de financement avec cumul d'aides identifié et plafonné",
      "Dépôt du dossier auprès de l'ADEME avant engagement des travaux",
      'Engagement de suivi de performance pendant 5 à 10 ans',
    ],
    cumulableWith: [
      'Aides travaux BAT / IND',
      'Fonds décarbonation industrie',
      'Avantage fiscal',
      'Aides régionales',
    ],
    faq: [
      {
        question: 'Comment candidater au Fonds chaleur ?',
        answer:
          "Trois voies : (1) appel à projets régional Biomasse pour les grosses installations, (2) appel à projets régional pour solaire thermique et géothermie, (3) contrat de développement pour les opérations multi-sites d'un même groupe. Le portail ADEME publie les fenêtres en cours.",
      },
      {
        question: 'Quel cumul avec les aides travaux est admis ?',
        answer:
          'Le cumul Fonds chaleur + aides travaux est explicitement admis dans la doctrine ADEME, dans la limite des plafonds autorisés. Les aides travaux valorisent les économies réalisées tandis que le Fonds chaleur finance la part équipement renouvelable.',
      },
      {
        question: 'Quelles études sont éligibles à un cofinancement ADEME ?',
        answer:
          "L'ADEME peut cofinancer les études de faisabilité préalables, surtout pour les réseaux de chaleur, la géothermie profonde et la biomasse industrielle. Un moyen de sécuriser le projet avant d'engager les investissements.",
      },
    ],
    ctaLabel: "Étudier l'éligibilité Fonds chaleur",
    ctaHref: '/contact?source=entreprises&besoin=fonds-chaleur',
    secondaryCta: { label: 'Comparer les aides', href: '/entreprises/aides' },
  },
  // ══════════════════════════════════════════════════════════════
  // 4. Fonds décarbonation industrie
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'fonds-decarbonation',
    iconName: 'banknote',
    eyebrow: 'Plan France 2030, Fonds décarbonation industrie,',
    title: 'Fonds décarbonation industrie (Bpifrance + ADEME)',
    shortLabel: 'Fonds décarbonation',
    lead: 'Une aide de France 2030 pour réduire les émissions de vos sites industriels. Électrification des process, efficacité énergétique, hydrogène bas carbone.',
    amount: 'Selon appel à projets',
    amountLabel: 'Subvention, avance remboursable, prêt selon volet',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Plusieurs guichets',
        description:
          'Bpifrance (innovation, financement décarbonation) et ADEME (énergétique, projets pilotes renouvelable industrielle).',
      },
      {
        iconName: 'sparkles',
        label: 'Subvention + avance remboursable',
        description:
          'Subvention directe pour la part qui ne génère pas de revenus, avance remboursable en cas de succès commercial du projet.',
      },
      {
        iconName: 'cog',
        label: 'Investissements structurants',
        description:
          'Substitution du gaz fossile par des énergies renouvelables industrielles, électrification de procédés, hydrogène bas carbone, capture du CO₂. Souvent projets > 5 M€.',
      },
      {
        iconName: 'shield-check',
        label: 'Encadrement européen',
        description:
          "Plafonds d'intensité d'aide encadrés. Notification à la Commission européenne pour les très gros projets.",
      },
    ],
    conditions: [
      'Site industriel en France, projet visant une réduction documentée des émissions',
      "Plan d'affaires solide démontrant la viabilité technique et économique",
      'Apport de fonds propres significatif (variable selon le dispositif)',
      "Dépôt dans la fenêtre d'appel à projets en cours",
      "Engagement à conserver l'investissement et à reporter les indicateurs de performance",
    ],
    cumulableWith: [
      'Aides IND',
      'Fonds chaleur ADEME',
      'Avantage fiscal',
      "Crédit d'impôt recherche",
    ],
    faq: [
      {
        question: 'Comment candidater au Fonds décarbonation ?',
        answer:
          "Selon le volet, le dépôt se fait sur le portail Bpifrance France 2030 (innovation, financement) ou directement auprès de l'ADEME (énergétique, projets pilotes). Les appels à projets sont publiés régulièrement — le suivi de calendrier est essentiel.",
      },
      {
        question: 'Mon entreprise en groupe est-elle éligible ?',
        answer:
          "Oui, mais l'analyse tient compte de la taille consolidée du groupe (PME, ETI ou grande entreprise). Cela impacte le taux d'intensité d'aide maximum applicable. Les projets de filière portés par plusieurs industriels peuvent bénéficier de modalités spécifiques.",
      },
      {
        question: "Quel délai d'instruction ?",
        answer:
          "Comptez plusieurs mois (6 à 12 mois) entre le dépôt et la notification de décision. C'est un dispositif qui se prépare en amont — d'où l'importance d'un montage dossier solide et d'un accompagnement spécialisé.",
      },
    ],
    ctaLabel: 'Étudier mon éligibilité',
    ctaHref: '/contact?source=entreprises&besoin=fonds-decarbonation',
    secondaryCta: { label: 'Voir tous les leviers', href: '/entreprises/aides' },
  },
  // ══════════════════════════════════════════════════════════════
  // 5. Suramortissement fiscal
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'suramortissement',
    iconName: 'percent',
    eyebrow: 'Avantage fiscal, Code général des impôts,',
    title: 'Suramortissement fiscal (art. 39 quinquies AA & art. 39 decies)',
    shortLabel: 'Suramortissement',
    lead: "Vous pouvez déduire de votre impôt une partie du coût de certains équipements énergétiques. Une économie fiscale qui s'ajoute aux aides.",
    amount: 'Déduction fiscale',
    amountLabel: "Sur l'IS — selon dispositif et année d'éligibilité",
    keyPoints: [
      {
        iconName: 'percent',
        label: 'Production de chaleur',
        description:
          "Équipements de production d'énergie utilisant les énergies renouvelables ou de récupération, et certains équipements d'efficacité énergétique.",
      },
      {
        iconName: 'gauge',
        label: 'Pilotage et équipements industriels',
        description:
          'Pilotage intelligent tertiaire et industriel, robinets thermostatiques, équipements de stockage thermique.',
      },
      {
        iconName: 'cog',
        label: 'Robotique et RSE',
        description:
          'Certains équipements de robotique industrielle et équipements RSE peuvent être éligibles.',
      },
      {
        iconName: 'banknote',
        label: 'Avantage de trésorerie',
        description:
          "La déduction supplémentaire réduit la base d'impôt sur la durée d'amortissement. Avantage économique réel selon le taux d'impôt.",
      },
    ],
    conditions: [
      "Entreprise soumise à l'IS ou à l'IR au régime réel (BIC, BA, BNC)",
      'Équipement neuf, éligible au texte du CGI applicable',
      'Acquisition dans la fenêtre temporelle prévue par la loi de finances',
      "Inscription correcte à l'actif immobilisé et amortissement selon nature",
      'Documentation comptable et fiscale rigoureuse',
    ],
    cumulableWith: [
      'Aides BAT / IND',
      'Fonds chaleur ADEME',
      'Fonds décarbonation industrie',
      "Crédit d'impôt recherche",
    ],
    faq: [
      {
        question: 'Quels équipements sont éligibles ?',
        answer:
          "La liste évolue d'une loi de finances à l'autre. Sont historiquement visés : équipements de production de chaleur renouvelable, pilotage intelligent de niveau avancé, robinets thermostatiques, équipements industriels d'efficacité énergétique, robotique industrielle.",
      },
      {
        question: "Comment se calcule l'avantage fiscal ?",
        answer:
          "C'est une déduction supplémentaire du résultat imposable, généralement étalée sur la durée normale d'amortissement de l'équipement. L'économie d'impôt finale dépend du taux d'impôt appliqué (15 % ou 25 %). Parlez-en à votre expert-comptable avant l'investissement.",
      },
      {
        question: "L'avantage fiscal est-il cumulable avec une subvention ?",
        answer:
          "Oui, l'avantage fiscal s'applique à l'investissement net (après déduction des subventions reçues). Il n'entre pas dans les plafonds d'aide car ce n'est pas une aide directe. C'est l'un des leviers d'optimisation les plus puissants.",
      },
    ],
    ctaLabel: 'Étudier le suramortissement',
    ctaHref: '/contact?source=entreprises&besoin=suramortissement',
    secondaryCta: { label: 'Voir tous les leviers', href: '/entreprises/aides' },
  },
  // ══════════════════════════════════════════════════════════════
  // 6. Optimisation TURPE multi-sites
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement de l'électricité,",
    title: 'Optimisation TURPE multi-sites',
    shortLabel: 'Optimisation TURPE',
    lead: "Le transport de l'électricité jusqu'à vos sites a un coût. En analysant vos courbes de consommation, on trouve souvent des économies sans rien changer à vos contrats.",
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque point de livraison',
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Puissances par classe temporelle',
        description:
          'Les puissances se souscrivent par classe (pointe, heures pleines/creuses, saison). On recale chaque classe sur les appels de puissance réels mesurés par vos courbes de charge.',
      },
      {
        iconName: 'building-2',
        label: 'Approche consolidée multi-sites',
        description:
          "L'audit couvre l'ensemble du parc : chaque point de livraison a sa propre optimisation, et la vision consolidée alimente vos arbitrages.",
      },
      {
        iconName: 'trending-down',
        label: 'Dépassements et énergie réactive',
        description:
          "Les pénalités de dépassement et la facturation d'énergie réactive sont des postes souvent ignorés. On les identifie et on traite : ajustement ou compensation.",
      },
      {
        iconName: 'wallet',
        label: "Indépendant des fournisseurs d'énergie",
        description:
          "Le transport est identique quel que soit le fournisseur : l'optimisation se cumule avec la mise en concurrence des contrats et la stratégie d'achat.",
      },
    ],
    conditions: [
      'Analyse des courbes de charge ou des 12 derniers mois de factures par site',
      'Applicable à tous les segments (basse et haute tension), quel que soit le fournisseur',
      'Modification des paramètres via le fournisseur ou le gestionnaire de réseau',
      "Étude des pénalités de dépassement et de l'énergie réactive site par site",
    ],
    cumulableWith: ['Courtage énergie multi-sites', 'Exonération CSPE', 'Aides travaux BAT / IND'],
    faq: [
      {
        question: 'Quels paramètres sont optimisables sur un site ?',
        answer:
          "Principalement : les puissances souscrites par classe temporelle (à recaler sur les appels réels), la version tarifaire (courte/moyenne/longue utilisation selon le profil), les pénalités de dépassement et l'énergie réactive (compensation possible). Chaque point de livraison a son optimum.",
      },
      {
        question: "L'optimisation nécessite-t-elle de changer de contrat ?",
        answer:
          'Non. Le transport est régulé et identique chez tous les fournisseurs. Les ajustements se font sur les paramètres du point de livraison, sans toucher au contrat de fourniture. On mène souvent les deux chantiers en parallèle.',
      },
      {
        question: 'À quelle fréquence réviser les souscriptions ?',
        answer:
          "À chaque évolution significative du site (extension, nouvel équipement, changement d'organisation) et lors de chaque révision tarifaire. Un parc multi-sites gagne à mettre en place une revue annuelle systématique.",
      },
    ],
    ctaLabel: 'Demander un audit acheminement',
    ctaHref: '/entreprises/comparateur-energie',
    secondaryCta: {
      label: 'En parler avec un référent',
      href: '/contact?source=entreprises&besoin=optimisation-turpe',
    },
  },
  // ══════════════════════════════════════════════════════════════
  // 7. Exonération CSPE / accise sur l'électricité
  // ══════════════════════════════════════════════════════════════
  {
    slug: 'exoneration-cspe',
    iconName: 'percent',
    eyebrow: "Fiscalité de l'électricité, taux réduits et exonérations,",
    title: "Exonération CSPE & taux réduits d'accise",
    shortLabel: 'Exonération CSPE',
    lead: "Une taxe sur chaque kWh d'électricité consommé. Certaines entreprises industrielles y échappent ou paient moins. Si vous avez trop payé, c'est récupérable.",
    amount: 'Taux réduit ou exonération',
    amountLabel: "Selon l'électro-intensité et les usages du site",
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Électro-intensité calculée',
        description:
          "L'éligibilité aux taux réduits repose sur le ratio entre la consommation d'électricité et la valeur ajoutée. On calcule ce ratio à partir de vos données.",
      },
      {
        iconName: 'cog',
        label: 'Usages process exonérés',
        description:
          'Certains procédés sont exonérés par nature : électrolyse, réduction chimique, procédés métallurgiques et minéralogiques.',
      },
      {
        iconName: 'banknote',
        label: 'Réclamation sur le passé',
        description:
          'Si le taux plein a été appliqué à tort, la réclamation permet de récupérer le trop-versé sur les années récentes.',
      },
      {
        iconName: 'file-signature',
        label: 'Mise en conformité durable',
        description:
          'Attestation transmise aux fournisseurs pour appliquer le bon taux, procédure documentée pour les exercices suivants.',
      },
    ],
    conditions: [
      "Calcul du ratio d'électro-intensité (consommation rapportée à la valeur ajoutée)",
      'Identification des usages éligibles site par site (process exonérés, usages standards)',
      'Attestations transmises aux fournisseurs pour chaque point de livraison',
      'Dossier justificatif complet (factures, données comptables, descriptif des procédés) pour les réclamations',
    ],
    cumulableWith: [
      'Optimisation TURPE',
      'Courtage énergie multi-sites',
      'Aides IND',
      'Fonds décarbonation industrie',
    ],
    faq: [
      {
        question: 'CSPE, TICFE, accise : quelle différence ?',
        answer:
          "Trois noms pour une même taxe : la CSPE historique a été absorbée par la TICFE, rebaptisée depuis « accise sur l'électricité ». Elle figure sur chaque facture, proportionnelle aux MWh consommés.",
      },
      {
        question: 'Comment savoir si mon entreprise est électro-intensive ?',
        answer:
          "Le critère repose sur le rapport entre la consommation d'électricité et la valeur ajoutée de l'entreprise, avec des seuils fixés par les textes applicables. Le calcul se fait à partir de votre liasse fiscale et de vos factures.",
      },
      {
        question: "Sur combien d'années peut-on réclamer ?",
        answer:
          "Le délai de réclamation fiscale est limité — en pratique l'année en cours et les deux années précédentes. Chaque année qui passe fait perdre un exercice récupérable, d'où l'intérêt de vérifier l'éligibilité dès maintenant.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=entreprises&besoin=exoneration-cspe',
    secondaryCta: {
      label: 'Audit acheminement en parallèle',
      href: '/entreprises/comparateur-energie',
    },
  },
];

export function getAideEntrepriseStub(slug: string): AideStub | undefined {
  return AIDES_ENTREPRISES.find((a) => a.slug === slug);
}
